import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Sparkle, MagicWand, EnvelopeSimple, PaperPlaneTilt, CalendarPlus, ArrowRight, X,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/PageHeader";
import { avatarColor, initials, relTime, fmtTime, labelConfig } from "@/lib/format";
import { toast } from "sonner";

function LabelBadge({ label }) {
  const c = labelConfig(label);
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${c.cls}`}>
      {c.text}
    </span>
  );
}

export default function Inbox() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState(null);
  const [instruction, setInstruction] = useState("");
  const { data: emails, isLoading } = useQuery({ queryKey: ["inbox"], queryFn: api.inbox });

  const triage = useMutation({
    mutationFn: api.triage,
    onSuccess: (d) => { qc.setQueryData(["inbox"], d); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Inbox triaged by Auppo"); },
    onError: () => toast.error("Triage failed"),
  });

  const current = (emails || []).find((e) => e.id === openId);
  const untriaged = (emails || []).some((e) => !e.is_triaged);

  const summarize = useMutation({
    mutationFn: () => api.summarize(openId),
    onSuccess: (d) => {
      qc.setQueryData(["inbox"], (prev) => prev.map((e) => e.id === openId ? { ...e, ai_summary: d.summary } : e));
      toast.success("Thread summarized");
    },
  });

  const draft = useMutation({
    mutationFn: () => api.draft(openId, instruction),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["actions"] });
      setInstruction("");
      toast.success("Draft created — review it under Pending Actions", {
        action: { label: "Review", onClick: () => (window.location.href = "/actions") },
      });
    },
    onError: () => toast.error("Could not draft reply"),
  });

  const propose = useMutation({
    mutationFn: () => api.proposeTimes(openId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["actions"] });
      toast.success("Meeting times proposed — see Pending Actions", {
        action: { label: "Review", onClick: () => (window.location.href = "/actions") },
      });
    },
    onError: () => toast.error("Could not propose times"),
  });

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10">
      <PageHeader
        testid="inbox-header"
        eyebrow="Gmail"
        title="Inbox"
        subtitle={`${emails?.length || 0} messages · ${(emails || []).filter((e) => e.unread).length} unread`}
        actions={
          <Button
            data-testid="triage-btn"
            onClick={() => triage.mutate()}
            disabled={triage.isPending}
            className="rounded-xl bg-slate-900 hover:bg-slate-800 gap-2"
          >
            <MagicWand weight="fill" size={16} />
            {triage.isPending ? "Triaging…" : untriaged ? "Run AI triage" : "Re-triage"}
          </Button>
        }
      />

      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-slate-400 aria-thinking">Loading inbox…</div>
        ) : (
          (emails || []).map((e, i) => (
            <motion.button
              key={e.id}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
              data-testid={`email-row-${e.id}`}
              onClick={() => { setOpenId(e.id); api.email(e.id).then(() => qc.setQueryData(["inbox"], (p) => p.map((x) => x.id === e.id ? { ...x, unread: false } : x))); }}
              className={`w-full text-left flex items-start gap-4 px-5 py-4 border-b border-border last:border-0 hover:bg-slate-50 transition-colors ${e.unread ? "bg-white" : "bg-slate-50/40"}`}
            >
              <div className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${avatarColor(e.from_name)}`}>
                {initials(e.from_name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-sm truncate ${e.unread ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>{e.from_name}</span>
                  {e.ai_label && <LabelBadge label={e.ai_label} />}
                  {e.needs_scheduling && (
                    <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                      <CalendarPlus size={11} /> meeting
                    </span>
                  )}
                  <span className="ml-auto text-xs text-slate-400 shrink-0">{relTime(e.received_at)}</span>
                </div>
                <div className={`text-sm truncate ${e.unread ? "font-semibold text-slate-800" : "text-slate-600"}`}>{e.subject}</div>
                <div className="text-sm text-slate-400 truncate mt-0.5">{e.ai_summary || e.snippet}</div>
              </div>
            </motion.button>
          ))
        )}
      </div>

      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl p-0 overflow-y-auto aria-scroll" data-testid="email-detail">
          {current && (
            <div className="flex flex-col min-h-full">
              <SheetTitle className="sr-only">{current.subject}</SheetTitle>
              <SheetDescription className="sr-only">Email from {current.from_name}</SheetDescription>
              <div className="px-6 pt-6 pb-4 border-b border-border">
                <div className="flex items-start gap-2 mb-3">
                  {current.ai_label && <LabelBadge label={current.ai_label} />}
                  {typeof current.priority_score === "number" && (
                    <span className="text-[11px] text-slate-400">priority {current.priority_score}</span>
                  )}
                </div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 leading-snug pr-6">{current.subject}</h2>
                <div className="flex items-center gap-2.5 mt-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold ${avatarColor(current.from_name)}`}>
                    {initials(current.from_name)}
                  </div>
                  <div className="text-sm">
                    <div className="font-medium text-slate-800">{current.from_name}</div>
                    <div className="text-xs text-slate-400">{current.from_email}</div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-5 space-y-4 flex-1">
                <div className="bg-slate-50 border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 flex items-center gap-1.5">
                      <Sparkle weight="fill" size={12} /> AI summary
                    </span>
                    <button
                      data-testid="summarize-btn"
                      onClick={() => summarize.mutate()}
                      disabled={summarize.isPending}
                      className="text-xs text-slate-500 hover:text-slate-900"
                    >
                      {summarize.isPending ? "Summarizing…" : current.ai_summary ? "Refresh" : "Summarize thread"}
                    </button>
                  </div>
                  <div className={`text-sm text-slate-600 whitespace-pre-line ${summarize.isPending ? "aria-thinking" : ""}`}>
                    {current.ai_summary || "No summary yet — ask Auppo to summarize this thread."}
                  </div>
                </div>

                <div className="space-y-3">
                  {current.thread.map((m, i) => (
                    <div key={i} className="border border-border rounded-xl p-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-slate-800">{m.from_name}</span>
                        <span className="text-xs text-slate-400">{fmtTime(m.at)}</span>
                      </div>
                      <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">{m.body}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-border px-6 py-4 space-y-3">
                <Textarea
                  data-testid="draft-instruction-input"
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  placeholder="Optional: tell Auppo how to reply (e.g. 'politely decline, suggest next week')"
                  className="resize-none text-sm rounded-xl min-h-[60px]"
                />
                <div className="flex gap-2">
                  <Button
                    data-testid="draft-reply-btn"
                    onClick={() => draft.mutate()}
                    disabled={draft.isPending}
                    className="flex-1 rounded-xl bg-slate-900 hover:bg-slate-800 gap-2"
                  >
                    <PaperPlaneTilt weight="fill" size={16} />
                    {draft.isPending ? "Drafting…" : "Draft reply"}
                  </Button>
                  {current.needs_scheduling && (
                    <Button
                      data-testid="propose-times-btn"
                      onClick={() => propose.mutate()}
                      disabled={propose.isPending}
                      variant="outline"
                      className="rounded-xl border-indigo-300 text-indigo-700 hover:bg-indigo-50 gap-2"
                    >
                      <CalendarPlus weight="bold" size={16} />
                      {propose.isPending ? "Finding…" : "Propose times"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
